import React from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './StageMessage.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface StageMessageProps {
  /** Registry name of the 26px glyph. */
  icon: string;
  title: string;
  body: string;
  /** `StagePill`s. Laid out in a row under the copy, 8px apart. */
  actions?: React.ReactNode;
  theme?: HeaderTheme;
  className?: string;
}

/**
 * The centred block the stage shows when there is no artifact on it — nothing selected, or a render
 * that failed.
 *
 * One component for both, because the two are drawn as the same object with different words: a
 * 26px glyph at 40% over a 14px line and a 12px one, 340px wide, dead centre. Splitting them would
 * be two stylesheets that have to be kept identical by hand.
 *
 * The glyph is at 40% opacity rather than a fainter colour token so it dims uniformly against
 * whatever it is sitting on — this block is centred on the mat in dark and on a near-white mat in
 * light, and a fixed tone that reads as "quiet" on one is either invisible or heavy on the other.
 */
export function StageMessage({ icon, title, body, actions, theme: themeProp, className }: StageMessageProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div className={cn(themes[theme], styles.block, className)}>
      <DeckIcon className={styles.icon} name={icon} size={26} aria-hidden />
      <div className={styles.title}>{title}</div>
      <div className={styles.body}>{body}</div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}

export interface StagePillProps {
  label: string;
  /** `solid` is the one filled button in v1.5. `outline` is a rule and a label. */
  variant?: 'solid' | 'outline';
  /** Registry name of a 14px leading glyph. Only the empty state's CTA has one. */
  icon?: string;
  /**
   * `cta` is the empty state's invitation — taller, a size up, and quieter in weight, because it is
   * the only thing on the stage. `sm` is the failed state's pair, which sit under a sentence that
   * is already carrying the message.
   */
  size?: 'cta' | 'sm';
  onClick?: () => void;
  theme?: HeaderTheme;
  /** Stories and visual tests only: paints the hover state without a real pointer. */
  forceHover?: boolean;
  className?: string;
}

/**
 * The stage's own button. It lives beside `StageMessage` because it has exactly one home.
 *
 * Not `MenuRow` and not `IconSlot`: those are a row you land on and a glyph on a bar, and this is a
 * pill on open ground with a border and a label. `solid`'s ink is the pane's own colour rather than
 * black, so the filled button reads as a hole punched through the surface instead of a sticker laid
 * on it.
 */
export function StagePill({
  label,
  variant = 'outline',
  icon,
  size = 'sm',
  onClick,
  theme: themeProp,
  forceHover = false,
  className,
}: StagePillProps) {
  const theme = useV15Theme(themeProp);
  return (
    <button
      type="button"
      className={cn(
        themes[theme],
        styles.pill,
        styles[variant],
        styles[size],
        forceHover && styles.forceHover,
        className,
      )}
      onClick={onClick}
    >
      {icon ? <DeckIcon className={styles.pillIcon} name={icon} size={14} aria-hidden /> : null}
      {label}
    </button>
  );
}
